import { LoadingOutlined, ShareAltOutlined } from '@ant-design/icons'
import { modes, paneModes } from '@constants/orderWizard'
import { useLockedOrders } from '@hooks/useLockedOrders'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useModelConfig } from '@hooks/useModelConfig'
import { Checkbox, Col, Popconfirm, Popover, Row, Steps, Tooltip } from 'antd'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useOrderContainersContext } from '../Contexts/OrderContainersContext'
import { useSupplyOptionContext } from '../Contexts/SupplyOptionContext'
import { useWizardMaster } from '../Contexts/WizardMasterContext'
import { REQUIRED_SIMPLE_WINDOW_FIELDS } from '../Panes/DisplayPane/DisplayComponents/EditedAlert/AlertPanes/useCarrierWindowOverride'
import { isDefined } from '@/utils/general'

const { Step } = Steps

const PANE_MODES = [paneModes.PRODUCTSELECT, paneModes.SUPPLYFILTERS, paneModes.TANKIMPACT]

export function EntryStatus({ paneMode, mode, closeModal }) {
  const { config } = useModelConfig()
  const { nonOptimalChosen } = useSupplyOptionContext()
  const { payload, wizardOrder, isSaving, saveOrder, carrierWindowState, setPaneMode } = useWizardMaster()
  const { updateSetting } = useUserSettings()
  const { lockedOrders } = useLockedOrders()
  const optimizing = useMemo(() => {
    const ids = lockedOrders?.map((order) => order.order_id)
    return ids?.includes(wizardOrder?.id)
  }, [lockedOrders, wizardOrder])
  const getCurrent = (paneMode) => {
    switch (paneMode) {
      case paneModes.PRODUCTSELECT:
        return 0
      case paneModes.SUPPLYFILTERS:
        return 1
      case paneModes.TANKIMPACT:
        return 2
      default:
        return <> you done goofed</>
    }
  }

  const steps = [
    {
      title: 'Destinations/Products',
      value: paneModes.PRODUCTSELECT,
    },
    {
      title: 'Supply Option',
      value: paneModes.SUPPLYFILTERS,
    },
    {
      title: 'Review',
      value: paneModes.TANKIMPACT,
    },
  ]

  const canGoBack = paneMode !== paneModes.PRODUCTSELECT
  function onClickBackCancel() {
    if (canGoBack) {
      setPaneMode(PANE_MODES[getCurrent(paneMode) - 1])
    } else {
      closeModal()
    }
  }

  const isSimpleWindowIncomplete =
    carrierWindowState.inputMode === 'simple' &&
    REQUIRED_SIMPLE_WINDOW_FIELDS.some((field) => !isDefined(carrierWindowState.simpleWindow?.[field]))

  const isCustomWindowIncomplete =
    carrierWindowState.inputMode === 'custom' &&
    carrierWindowState.customWindow?.some((v) => !v || typeof v === 'undefined')

  const isFinalWindowIncomplete =
    carrierWindowState.isOverrideToggled && (isSimpleWindowIncomplete || isCustomWindowIncomplete)

  const createDisabled =
    isSaving ||
    paneMode !== paneModes.TANKIMPACT ||
    (nonOptimalChosen && payload && !payload.reason_name) ||
    isFinalWindowIncomplete

  const { trailerOverride } = useOrderContainersContext()
  const needsPopConfirm = wizardOrder?.carrier_notify_state === 'sent'

  return (
    <Row className='order-status p-3'>
      <Col span={6}>
        {wizardOrder?.number ? (
          <Horizontal verticalCenter>
            <Link
              target='_blank'
              to={`/order_movements_page/report?${new URLSearchParams({ order_number: wizardOrder.number })}`}
              onClick={() => {
                localStorage.setItem('current-order-movements-report-view', 'all')
                updateSetting('current-order-movements-report-view', 'all')
              }}
            >
              <GraviButton icon={<ShareAltOutlined />} buttonText='View Movement' />
            </Link>
          </Horizontal>
        ) : (
          <Texto category='h5'>Order: N/A</Texto>
        )}
      </Col>
      <Col span={14}>
        <Steps current={getCurrent(paneMode)}>
          {steps.map((item) => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
      </Col>
      <Col span={4}>
        <Row>
          <Col span={12}>
            <GraviButton
              buttonText={canGoBack ? 'Back' : 'Cancel'}
              appearance='outline'
              onClick={onClickBackCancel}
              style={{ float: 'right' }}
            />
          </Col>
          <Tooltip
            title={isFinalWindowIncomplete ? 'Carrier window is missing required fields' : ''}
            placement='topRight'
          >
            <Col span={12} align='right'>
              {needsPopConfirm ? (
                <ResendPopover
                  closeModal={closeModal}
                  saveOrder={saveOrder}
                  trailerOverride={trailerOverride}
                  createDisabled={createDisabled}
                  optimizing={optimizing}
                  isSaving={isSaving}
                  mode={mode}
                  wizardOrder={wizardOrder}
                  config={config}
                />
              ) : (
                <GraviButton
                  theme2
                  buttonText={mode === modes.CREATE ? 'Create Order' : 'Save Order'}
                  data-cy='save-order-btn'
                  disabled={createDisabled || optimizing}
                  icon={isSaving && <LoadingOutlined spin />}
                  onClick={() => saveOrder({ closeModal, resendToCarrier: false, trailerOverride })}
                />
              )}
            </Col>
          </Tooltip>
        </Row>
      </Col>
    </Row>
  )
}

function ResendPopover({
  closeModal,
  saveOrder,
  trailerOverride,
  createDisabled,
  optimizing,
  isSaving,
  mode,
  wizardOrder,
  config,
}) {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false)
  const [resendToCarrier, setResendToCarrier] = useState(true)
  const [resendToSupplier, setResendToSupplier] = useState(true)

  if (!config.send_supplier_emails || !wizardOrder.has_supplier_emails || wizardOrder?.supply_option?.delivered) {
    return (
      <Popconfirm
        className='carrier-warning-message'
        placement='topRight'
        title='Would you like to resend emails?'
        onConfirm={() => saveOrder({ closeModal, resendToCarrier: true, trailerOverride })}
        onCancel={() => saveOrder({ closeModal, resendToCarrier: false })}
        okText='Yes, resend'
        cancelText='No'
      >
        <GraviButton
          theme2
          disabled={createDisabled || optimizing}
          icon={isSaving && <LoadingOutlined spin />}
          buttonText={mode === modes.CREATE ? 'Create Order' : 'Save Order'}
        />
      </Popconfirm>
    )
  }

  return (
    <Popover
      open={isPopoverVisible}
      onOpenChange={setIsPopoverVisible}
      trigger='click'
      placement='topRight'
      content={
        <Vertical style={{ minWidth: 300 }}>
          <Texto category='h6' className='mb-2'>
            Would you like to resend emails?
          </Texto>
          <Horizontal style={{ gap: '0.5rem' }}>
            <Checkbox value={resendToCarrier} onChange={(e) => setResendToCarrier(e.target.checked)} defaultChecked />
            <Texto>Send Update to Carrier?</Texto>
          </Horizontal>
          <Horizontal style={{ gap: '0.5rem' }}>
            <Checkbox value={resendToSupplier} onChange={(e) => setResendToSupplier(e.target.checked)} defaultChecked />
            <Texto>Send Update to Supplier?</Texto>
          </Horizontal>
          <Horizontal justifyContent='flex-end' style={{ gap: '0.5rem' }} className='mt-2'>
            <GraviButton
              buttonText='No'
              appearance='outline'
              size='small'
              onClick={() => {
                saveOrder({ closeModal, resendToCarrier: false, trailerOverride })
              }}
            />
            <GraviButton
              disabled={!resendToCarrier && !resendToSupplier}
              buttonText='Yes, resend'
              theme1
              size='small'
              onClick={() => {
                saveOrder({ closeModal, resendToCarrier, resendToSupplier, trailerOverride })
              }}
            />
          </Horizontal>
        </Vertical>
      }
    >
      <GraviButton
        theme2
        disabled={createDisabled || optimizing}
        icon={isSaving && <LoadingOutlined spin />}
        buttonText={mode === modes.CREATE ? 'Create Order' : 'Save Order'}
      />
    </Popover>
  )
}
